/**
 * 词表（S08）：左侧树浏览，右侧看节点详情与规格模板。
 *
 * 这一页的产品价值不在"能看树"，而在**选中节点就带出该节点的规格字段**（马达 → 功率/转速/扭矩）。
 * 后续 S10（URDF/关节元数据）、S11（资产建版本）都从这里取"分类 → 规格模板"。
 *
 * 词表本身只读：改词表回改 xmind，跑 tools/xmind2json.py + tools/taxonomy-seed.mjs。
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Breadcrumb,
  Card,
  Col,
  Descriptions,
  Empty,
  Row,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { callApi } from "../lib/api";
import { TaxonomyPicker, type SpecField, type TaxonomyNode } from "../components/TaxonomyPicker";
import { AsyncBoundary } from "../components/AsyncBoundary";

interface DetailData {
  node: TaxonomyNode;
  ancestors: TaxonomyNode[];
  children: TaxonomyNode[];
}

const TYPE_LABEL: Record<SpecField["type"], string> = {
  number: "数值",
  text: "文本",
  select: "单选",
  boolean: "是/否",
};

export function TaxonomyPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [onlySpec, setOnlySpec] = useState(false);

  const detail = useQuery({
    queryKey: ["taxonomy", "node", selectedId],
    queryFn: () => callApi<DetailData>(`/api/taxonomy/${selectedId}`),
    enabled: !!selectedId,
  });

  const node = detail.data?.data?.node;
  const fields = node?.spec_schema?.fields ?? [];

  const columns: ColumnsType<SpecField> = [
    {
      title: "字段",
      dataIndex: "label",
      render: (_, f) => (
        <Space direction="vertical" size={0}>
          <span>{f.label}</span>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {f.key}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: "类型",
      dataIndex: "type",
      width: 90,
      render: (t: SpecField["type"]) => <Tag>{TYPE_LABEL[t] ?? t}</Tag>,
    },
    {
      title: "单位",
      dataIndex: "unit",
      width: 80,
      render: (u?: string) => u || "-",
    },
    {
      title: "取值",
      dataIndex: "options",
      render: (opts?: string[], f?: SpecField) =>
        opts?.length ? (
          <Space size={4} wrap>
            {opts.map((o) => (
              <Tag key={o}>{o}</Tag>
            ))}
          </Space>
        ) : (
          <Typography.Text type="secondary">{f?.placeholder ?? "-"}</Typography.Text>
        ),
    },
    {
      title: "必填",
      dataIndex: "required",
      width: 70,
      render: (r?: boolean) => (r ? <Tag color="red">必填</Tag> : <span style={{ color: "#bbb" }}>可选</span>),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card
        styles={{ body: { padding: 20 } }}
        title={
          <Space size={10}>
            <span>词表</span>
            <Tag color="blue">S08</Tag>
          </Space>
        }
        extra={
          <Space size={8}>
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              只看有规格模板的节点
            </Typography.Text>
            <Switch size="small" checked={onlySpec} onChange={setOnlySpec} />
          </Space>
        }
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          来源：<code>docs/reference/机器人链接初版.xmind</code>（四大分支：系统 / 标准件 / 算法 / 通讯协议）。
          词表只读——改词表要回改 xmind，再跑 <code>python tools/xmind2json.py</code> 与{" "}
          <code>node tools/taxonomy-seed.mjs --apply</code>，避免库和源文件各说一套。
        </Typography.Paragraph>
      </Card>

      <Row gutter={16}>
        <Col xs={24} lg={10}>
          <Card title="选择分类" styles={{ body: { padding: 12 } }}>
            <TaxonomyPicker
              selectedId={selectedId}
              onlyWithSpec={onlySpec}
              onSelect={(n) => setSelectedId(n.id)}
            />
          </Card>
        </Col>

        <Col xs={24} lg={14}>
          <Card
            title={node ? node.name : "节点详情"}
            extra={
              node?.spec_schema ? (
                <Tag color="blue">{fields.length} 个规格字段</Tag>
              ) : null
            }
          >
            {!selectedId ? (
              <Empty description="从左侧选一个节点，查看它的规格模板" style={{ padding: 40 }} />
            ) : (
              <AsyncBoundary loading={detail.isLoading} error={detail.error}>
                {node && (
                  <Space direction="vertical" size={16} style={{ width: "100%" }}>
                    <Breadcrumb
                      items={[
                        ...(detail.data?.data?.ancestors ?? []).map((a) => ({
                          title: (
                            <a onClick={() => setSelectedId(a.id)} role="button">
                              {a.name}
                            </a>
                          ),
                        })),
                        { title: <strong>{node.name}</strong> },
                      ]}
                    />

                    <Descriptions size="small" column={2} bordered>
                      <Descriptions.Item label="层级" span={1}>
                        第 {node.depth} 级
                      </Descriptions.Item>
                      <Descriptions.Item label="子节点" span={1}>
                        {detail.data?.data?.children?.length ?? 0} 个
                      </Descriptions.Item>
                      <Descriptions.Item label="路径" span={2}>
                        <Space size={8}>
                          <Typography.Text code copyable={{ text: node.path_text }}>
                            {node.path_text}
                          </Typography.Text>
                          <a
                            role="button"
                            onClick={() => {
                              navigator.clipboard?.writeText(node.path_text);
                              message.success("路径已复制（可直接存进 asset_part.taxonomy_path）");
                            }}
                          >
                            复制
                          </a>
                        </Space>
                      </Descriptions.Item>
                      <Descriptions.Item label="xmind 节点" span={2}>
                        <Typography.Text type="secondary">{node.source_id ?? "-"}</Typography.Text>
                      </Descriptions.Item>
                    </Descriptions>

                    {node.spec_schema ? (
                      <>
                        {node.spec_schema.summary && (
                          <Alert type="info" showIcon message={node.spec_schema.summary} />
                        )}
                        <Table<SpecField>
                          size="small"
                          rowKey="key"
                          pagination={false}
                          columns={columns}
                          dataSource={fields}
                        />
                      </>
                    ) : (
                      <Alert
                        type="warning"
                        showIcon
                        message="该节点还没配规格模板"
                        description={
                          <span>
                            在 <code>packages/taxonomy/spec-templates.json</code> 里按路径补一条，再跑{" "}
                            <code>node tools/taxonomy-seed.mjs --apply</code>。
                          </span>
                        }
                      />
                    )}

                    {(detail.data?.data?.children?.length ?? 0) > 0 && (
                      <div>
                        <Typography.Text type="secondary">子节点：</Typography.Text>
                        <Space size={4} wrap style={{ marginTop: 6 }}>
                          {detail.data?.data?.children?.map((c) => (
                            <Tag
                              key={c.id}
                              style={{ cursor: "pointer" }}
                              color={c.spec_schema ? "blue" : undefined}
                              onClick={() => setSelectedId(c.id)}
                            >
                              {c.name}
                            </Tag>
                          ))}
                        </Space>
                      </div>
                    )}
                  </Space>
                )}
              </AsyncBoundary>
            )}
          </Card>
        </Col>
      </Row>
    </Space>
  );
}
